import { BadgeVariants } from '@packages/ui/shared';
import colors from '@packages/core/styles/colors';

export interface VINRecord {
    fullVIN?: string;
    vin: string;
    model: string;
    modelYear: number;
    modelNumber: string;
    colorCategory: string;
    colorDescription?: string;
    warrantyStartDt?: string;
    productionDate?: string;
    engine?: string;
    engineId?: string;
    engineSize?: number;
    vehicleTransmissionNumber?: string;
    transmissionType?: string;
    emissionSpec?: string;
    turboIndicator?: string;
    trimDescription?: string;
    driveTrain?: string;
    vinStatus?: string;
    optionCode?: string;
    fuel?: string;
    transmission?: string;
}

export interface EVBattery {
    bin?: string;
    eventDate?: string;
}

export interface EVBattery {
    bin?: string;
    eventDate?: string;
}

export interface EVBattery {
    bin?: string;
    eventDate?: string;
}

export interface EVBattery {
    bin?: string;
    eventDate?: string;
}

export interface PublicationType {
    id: string;
    code: string;
    label: string;
    description: string;
}

/** A carline as `/docs/vehicle/carline` returns it. */
export type VehicleCarline = {
    vehicleCarlineId: string;
    code: string;
    description: string;
};

/**
 * One vehicle row from `/docs/vehicles`. A trim is the engine, transmission and drivetrain
 * together, so each row is one trim of its carline and model year.
 */
export type ApplicableVehicle = {
    vehicleId: string;
    modelYear: number;
    vehicleCarline?: VehicleCarline | null;
    vehicleEngine?: { description: string } | null;
    vehicleTransmission?: { description: string } | null;
    vehicleDrivetrain?: { description: string } | null;
};

/**
 * The enriched details of a vehicle, including its VIN record, vehicle image url,
 * EV battery details, and eligibility for certain features.
 */
export type EnrichedVehicleDetails = {
    /** The VIN record of the vehicle. */
    vinRecord: VINRecord;

    /** The URL of the vehicle's image. */
    vehicleImageUrl: string;

    /** The ID of the carline. */
    carlineId: string;

    /** Whether the vehicle is eligible for CVT. */
    cvtEligible: boolean;

    /** The EV battery details of the vehicle, if applicable. */
    evBattery?: EVBattery;
};

export type VIN = Omit<EnrichedVehicleDetails, 'cvtEligible' | 'evBattery'>;

export type FetchVinResponse = EnrichedVehicleDetails[];

export type FetchVinResponseParams = {
    vin: string;
};

export interface OpenVinSessionParamsBody {
    vin: string;
    createdAccountId?: string;
}

export type AccountDocsType = 'RECENT' | 'FAVORITE';

export type RecentlyViewed = {
    vehicleDetails: EnrichedVehicleDetails;
    vinSession: VinSession;
};

export type VinSession = {
    active: boolean;
    vin: string;
    vinSessionEndTimestamp: string | null;
    vinSessionId: string;
    vinSessionStartTimestamp?: string;
};

/**
 * The lifecycle of a VIN session according to when it started and whether its
 * analytics event (end or abandon) has fired. Distinct from `VinSession`, which
 * does not track the start time and can have multiple instances of the same VIN session
 * (e.g., if a user opens the same VIN in multiple tabs).
 */
type VinSessionLifecycle = {
    startedAt: number;
    hasEnded: boolean;
};

export interface VinSessionState {
    selectedVin: EnrichedVehicleDetails | undefined;
    isVinLoading: boolean;
    selectedFilters: string[];
    title: string;
    vinBannerOpen: boolean;
    vinQmrsCount: {
        qmrCount: number;
        nqrCount: number;
        escalationCount: number;
        investigationCount: number;
        totalCount: number;
    };
    publicationsCount: number;
    vinTechlineCasesCount: number;
    toast: {
        toastMessage: string;
        iconName: string;
        iconColor: keyof typeof colors;
        backgroundColor?: keyof typeof colors;
    };
    hasPublicationsCountFetched: boolean;
    hasQmrsCountFetched: boolean;
    hasVinDataFetched: boolean;
    sessionLifecycle: VinSessionLifecycle | null;
}

export interface PageDetail {
    size: number;
    number: number;
    totalElements: number;
    totalPages: number;
}

export interface StisDocument {
    documentId: string;
    fileName: string;
    docType: string;
    title: string;
    docCode: string;
    createdDate: string;
    vin?: string;
    docTypeId: string;
    description: string;
    pubVersion: string;
    lastUpdated?: string | null;
    /** When this account last viewed the document. Only sent for the Recently Viewed list. */
    lastViewedAt?: string | null;
    isFavorite?: boolean;
    status: PublicationStatusEnum;
    toolNumber?: string;
    applicability?: string;
    visibilityLevel: string;
    hasVersions?: boolean;
}

export interface PublicationsDto {
    content: StisDocument[];
    page: {
        size: number;
        totalElements: number;
        totalPages: number;
        number: number;
    };
}

export interface StisDocumentCount {
    vin: string;
    docCount: number;
}

export interface StisQaSideNavCount {
    qmrCount: number;
    nqrCount: number;
    escalationCount: number;
    investigationCount: number;
    totalCount: number;
}

export interface FetchAccountParams {
    accountId: string | undefined;
}

export interface FavoriteDocument {
    vin: string;
    docType: string;
    title: string;
    docCode: string;
    createdDate: string;
    description: string;
    pubVersion: string;
    lastUpdated: string;
}

export interface PublicationUsersAccount {
    accountId: string;
    accountStatusId?: PublicationGroupStatus;
    name: string;
    firstName: string;
    lastName: string;
    timeZone: string;
    locale: string;
}

export interface ContentFile {
    fileName: string;
    fileType: string;
    createdDate: string;
    createdTimestamp: string;
    createdBy?: string;
}

export interface PublicationForm {
    documentId?: string;
    title: string;
    docCode: string;
    visibilityLevel: VisibilityTypeOption;
    url: string;
    contentFile?: ContentFile;
    createdDate: Date | null | string;
    keywords: string[];
    dtcCodes: string[];
    toolNumbers: string[];
    description: string;
    docType?: string;
    docTypeId?: string;
    status: string;
    publicationType: PublicationType;
    createdAccount?: PublicationUsersAccount;
    lastUpdatedAccount?: PublicationUsersAccount;
    createdBy?: string;
    updatedBy?: string;
    lastUpdated?: string;
    createdTimestamp?: string;
    createdTimestampForDisplay?: string;
    lastUpdatedTimestamp?: string;
    lastUpdatedTimestampForDisplay?: string;
    pubVersion: number;
    parentDocumentId?: string;
    uploadStatus?: string;
    documentsVersions?: DocumentVersion[];
    associateVehicleChange?: boolean;
    associatePublicationChange?: boolean;
    fileUploadChange?: boolean;
    tagsChange?: boolean;
    documentsVehicles?: any;
    associatedDocuments?: any;
    publishedYear?: number;
    publishedMonth?: number;
    interactiveInd?: boolean;
}

export interface DocumentVersion {
    documentId: string;
    title: string;
    docCode: string;
    docType: string;
    version: string;
    createdDate: string;
    lastUpdated: string;
    status: PublicationGroupStatus;
}

export enum StisPublicationAction {
    Edit = 'EDIT',
    New = 'NEW',
    AddVersion = 'ADD_VERSION',
}

export enum PublicationStatusEnum {
    All = 'ALL',
    Draft = 'DRAFT',
    InReview = 'IN_REVIEW',
    Published = 'PUBLISHED',
    Archived = 'ARCHIVED',
}

export enum PublicationActionsEnum {
    Duplicate = 'DUPLICATE',
    Delete = 'DELETE',
}

export enum PublicationGroupStatus {
    Published = 'PUBLISHED',
    Draft = 'DRAFT',
    InReview = 'IN_REVIEW',
    Archived = 'ARCHIVED',
}

export const statusVariants: Record<string, BadgeVariants> = {
    [PublicationGroupStatus.Draft]: 'default',
    [PublicationGroupStatus.Published]: 'green',
    [PublicationGroupStatus.InReview]: 'yellow',
    [PublicationGroupStatus.Archived]: 'red',
};

export type VisibilityType = 'PUBLIC' | 'DEALER' | 'FREE' | 'DATA_LOAD_ITEM' | 'DEACTIVATED' | 'IN_REVIEW' | '';

export interface VisibilityTypeOption {
    label: string;
    value: VisibilityType;
}

export const visibilityTypeOptions: VisibilityTypeOption[] = [
    { label: 'Public', value: 'PUBLIC' },
    { label: 'Dealer', value: 'DEALER' },
    { label: 'Free', value: 'FREE' },
];

export interface PreviewContent {
    documentId: string;
    documentPageId: string;
    pageNumber: number;
    headline: string;
    url: string;
}

export type PublicationPreviewContent = {
    documentId: string;
    docType?: string;
    createdDate?: string;
    lastUpdated?: string;
    fileName?: string;
    title: string;
    query: string;
    totalElements: number;
    totalPages: number;
    content: PreviewContent[];
};

export interface PreviewHeaderResponse {
    content: PreviewContent[];
    page: {
        size: number;
        number: number;
        totalElements: number;
        totalPages: number;
    };
}

export type ServiceDiagnostics = {
    carlineId: string;
    carlineDescription: string;
    documentId: string;
};

export interface ServiceDiagnosticsResponse {
    [year: string]: ServiceDiagnostics[];
}

/** One manual in the Service Diagnostics list. `matchCount` and `rank` are always null; manuals are not ranked. */
export interface ServiceDiagnosticsBundle {
    rank: number | null;
    matchCount: number | null;
    bundleId: string;
    documentId: string;
    bundleName: string;
    title: string;
    docCode: string | null;
    description: string | null;
    pubVersion: string | null;
    visibilityLevel: string | null;
    sectionCount: number | null;
    createdDate: string | null;
    lastUpdated: string | null;
    isFavorite: boolean | null;
    isRecent: boolean | null;
}

/** One matching page inside a manual, behind the eye icon. */
export interface ServiceDiagnosticsSection {
    rank: number | null;
    sectionId: string;
    bundleId: string;
    documentId: string;
    bundleName: string;
    title: string;
    /** Breadcrumb. Null for front matter and for every BRZ page. */
    titlePath: string | null;
    url: string | null;
    /** Absolute link that opens the manual at this page. */
    cdnUrl: string | null;
    headline: string | null;
    matchType: string | null;
    isFavorite: boolean | null;
    isRecent: boolean | null;
}

export interface ServiceDiagnosticsBundlesDto {
    content: ServiceDiagnosticsBundle[];
    page: PageDetail;
}

export interface ServiceDiagnosticsSectionsDto {
    content: ServiceDiagnosticsSection[];
    page: PageDetail;
}

export interface CarlineModelYearRange {
    carline: string;
    modelYearFrom: number | null;
    modelYearTo: number | null;
}

export interface TroubleshootingSearchRequest {
    vins?: string[];
    startDate?: string;
    endDate?: string;
    repairOrderNumber?: string;
    completion?: number;
    technician?: string;
    dtcs?: string[];
    carlineModelYearRanges?: CarlineModelYearRange[];
    odometerStart?: number;
    odometerEnd?: number;
    vinGetStatus?: number;
    odometerUnit?: string;
    retailerCode?: string;
    saveId?: string;
    page?: number;
    pageSize?: number;
}

export interface TroubleshootingSearchResult {
    soaUserId: string;
    saveId: string;
    searchId: number;
    troubleShootingId: string;
    userName: string;
    fullName: string;
    dealerNumber: string;
    repairOrderNumber: string;
    vin: string;
    pubNo: string;
    version: string;
    pageId: string;
    dtc: string;
    carline: string;
    carlineName: string;
    carlineDesc: string;
    modelYear: number;
    vinModelYear: number;
    vinCarlineName: string;
    vinGetStatus: number;
    validVinIndicator: number;
    vinMismatchIndicator: number;
    completion: number;
    contentPath: string;
    serviceManualPath: string;
    odometer: number;
    odometerUnit: string;
    documentId: string;
    visibility: string;
    createdDate: string;
    updatedDate: string;
    troubleShootingLog: Record<string, any>;
    odometerGetStatus: number;
}

export interface TroubleshootingSearchResponse {
    errorCode: string;
    message: string;
    result: {
        data: TroubleshootingSearchResult[];
        totalCount?: number;
        page?: number;
        pageSize?: number;
    };
}

// The endpoint returns no documentId, so a Quick Reference can only be identified by title
// and version. downloadUrl is pre-signed with a 5 minute expiry.
export interface QuickReference {
    documentTitle: string;
    version: string | null;
    downloadUrl: string | null;
}

export interface QuickReferenceResponse {
    documents: QuickReference[];
    pageNumber: number;
    pageSize: number;
    totalElements: number;
    totalPages: number;
    hasNext: boolean;
    hasPrevious: boolean;
}

export interface DocumentCodeSuggestion {
    code: string;
    title: string;
    publicationType: string;
    moreCount: number;
}

export interface DocumentCodeSuggestionResponse {
    suggestions: DocumentCodeSuggestion[];
    hasMore: boolean;
}

export type CvtConcernDoc = {
    documentCode: string;
    title: string;
    preSignedUrl: string;
};
